import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api, ApiError } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { Camp, Sectiune, BaraSalvare } from '@/ui/form'
import { PartenerPicker } from '@/ui/partner-picker'
import { MONEDE, azi } from '@/lib/domeniu'
import { CursSelector, citesteMod } from '@/ui/curs-selector'
import { cursDeReferinta, lei, type ContextCurs, type ModCurs } from '@/lib/money'

const TIPURI = [
  { v: 'Sales', t: 'Vanzare (client)' },
  { v: 'Purchase', t: 'Cumparare (furnizor)' },
  { v: 'Proforma', t: 'Proforma' },
  { v: 'Advance', t: 'Avans' },
]

export function FacturaNoua() {
  const nav = useNavigate()
  const qc = useQueryClient()
  const [f, setF] = useState({
    partner: '', invoice_type: 'Sales', number: '', total_value: '', currency: 'EUR',
    issue_date: azi(), due_date: '', service: '', booking_number: '', notes: '', vat_no: '',
  })
  const [mod, setMod] = useState<ModCurs>(citesteMod())
  const [manual, setManual] = useState<number | null>(null)
  const [eroare, setEroare] = useState<string | null>(null)
  const s = (k: keyof typeof f) => (v: string) => setF((x) => ({ ...x, [k]: v }))

  const { data: curs } = useQuery({
    queryKey: ['curs-bnr'],
    queryFn: () => api.get<{ eur_rate: number }>('/invoices/page', { per_page: 1 }),
    staleTime: 30 * 60_000,
  })
  const ctx: ContextCurs = { bnrPlus2: curs?.eur_rate ?? 0, mod, manual }
  const cursFolosit = useMemo(
    () => cursDeReferinta({ currency: f.currency, invoice_type: f.invoice_type, exchange_rate: null }, ctx),
    [f.currency, f.invoice_type, ctx])

  const valoare = Number(String(f.total_value).replace(',', '.')) || 0
  const inLei = f.currency === 'RON' ? valoare : valoare * cursFolosit

  const salveaza = useMutation({
    mutationFn: () => {
      if (!f.partner.trim()) throw new ApiError(400, 'Partenerul e obligatoriu')
      if (valoare <= 0) throw new ApiError(400, 'Valoarea trebuie sa fie mai mare ca zero')
      return api.post<{ id: number }>('/invoices/', {
        partner: f.partner,
        vat_no: f.vat_no || undefined,
        invoice_type: f.invoice_type,
        total_value: valoare,
        currency: f.currency,
        exchange_rate: f.currency === 'RON' ? undefined : cursFolosit,
        number: f.number || undefined,
        issue_date: f.issue_date || undefined,
        due_date: f.due_date || undefined,
        service: f.service || undefined,
        booking_number: f.booking_number || undefined,
        notes: f.notes || undefined,
      })
    },
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      nav(r?.id ? `/bani/facturi/${r.id}` : '/bani/facturi')
    },
    onError: (e) => setEroare(e instanceof Error ? e.message : 'Nu s-a putut salva'),
  })

  return (
    <>
      <PageHeader title="Factura noua"
        actions={<CursSelector ctx={ctx} onMod={setMod} onManual={setManual} />} />
      <div className="px-8 pb-0">
        <Sectiune titlu="Document" descriere="Numarul se genereaza singur daca il lasi gol.">
          <Camp eticheta="Tip" tip="select" valoare={f.invoice_type} seteaza={s('invoice_type')} optiuni={TIPURI} />
          <Camp eticheta="Numar" valoare={f.number} seteaza={s('number')} indiciu="gol = numerotare automata" />
          <Camp eticheta="Data emiterii" tip="date" valoare={f.issue_date} seteaza={s('issue_date')} />
          <Camp eticheta="Scadenta" tip="date" valoare={f.due_date} seteaza={s('due_date')} />
        </Sectiune>

        <Sectiune titlu="Partener" descriere="Clientul sau furnizorul de pe document.">
          <PartenerPicker eticheta={f.invoice_type === 'Purchase' ? 'Furnizor' : 'Client'}
            valoare={f.partner} seteaza={s('partner')} obligatoriu
            pentruRezervare={f.invoice_type !== 'Purchase'}
            alege={(p) => setF((x) => ({ ...x, vat_no: p.vat_no ?? p.cnp ?? x.vat_no }))} />
          <Camp eticheta="CUI / CNP" valoare={f.vat_no} seteaza={s('vat_no')} />
          <Camp eticheta="Nr. rezervare" valoare={f.booking_number} seteaza={s('booking_number')} />
        </Sectiune>

        <Sectiune titlu="Bani"
          descriere={valoare > 0
            ? `${lei(inLei)} lei${f.currency !== 'RON' ? ` la cursul ${cursFolosit.toFixed(4)}` : ''}`
            : 'Suma si moneda documentului.'}>
          <Camp eticheta="Valoare" valoare={f.total_value} seteaza={s('total_value')} obligatoriu />
          <Camp eticheta="Moneda" tip="select" valoare={f.currency} seteaza={s('currency')} optiuni={MONEDE} />
          <Camp eticheta="Serviciu" valoare={f.service} seteaza={s('service')} latime="plin" />
          <Camp eticheta="Note" tip="textarea" valoare={f.notes} seteaza={s('notes')} latime="plin" />
        </Sectiune>

        <BaraSalvare salveaza={() => { setEroare(null); salveaza.mutate() }}
          seSalveaza={salveaza.isPending} eroare={eroare} eticheta="Salveaza factura" />
      </div>
    </>
  )
}
